import { ReportCommentWithName } from '@helloself/hello-self-ui';
import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { getMemberReport } from '../../../../api/report/report';
import { PartnerReportData } from '../../../../api/report/types';
import { AppDispatch, RootState } from '../../../../app/store';
import { stateNeedsUpdating } from '../../../../reduxHelpers/stateHelpers';
import { emptyReturnObject, RequestStatusWithDetails } from '../../../../types/RequestStatusWithDetails';
import { ReportsSlice } from '../slice';

export const asyncLoadReport = createAsyncThunk<
    PartnerReportData,
    { reportId: number; force?: boolean },
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>(
    'reports/loadReport',
    async (args) => {
        return await getMemberReport(args.reportId);
    },
    {
        condition: (args, { getState }) => {
            const { reports } = getState();

            const report = reports.memberReports[args.reportId];

            return !!args.force || stateNeedsUpdating(report);
        },
    }
);

const reducer = (builder: ActionReducerMapBuilder<ReportsSlice>) => {
    builder.addCase(asyncLoadReport.pending, (state, action) => {
        const existingReportState = state.memberReports[action.meta.arg.reportId] ?? emptyReturnObject();
        state.memberReports[action.meta.arg.reportId] = {
            ...existingReportState,
            status: existingReportState.loadedTimestamp ? 'reloading' : 'pending',
        } as RequestStatusWithDetails<PartnerReportData>;

        const existingCommentsState = state.reportComments[action.meta.arg.reportId] ?? emptyReturnObject();
        state.reportComments[action.meta.arg.reportId] = {
            ...existingCommentsState,
            status: existingCommentsState.loadedTimestamp ? 'reloading' : 'pending',
        } as RequestStatusWithDetails<ReportCommentWithName[]>;
    });

    builder.addCase(asyncLoadReport.rejected, (state, action) => {
        state.memberReports[action.meta.arg.reportId].status = 'error';
        state.reportComments[action.meta.arg.reportId].status = 'error';
    });

    builder.addCase(asyncLoadReport.fulfilled, (state, action) => {
        const { reportComments, ...reportDataWithoutComments } = action.payload;

        state.memberReports[action.meta.arg.reportId] = {
            status: 'success',
            loadedTimestamp: Date.now(),
            data: reportDataWithoutComments,
        };

        state.reportComments[action.meta.arg.reportId] = {
            status: 'success',
            loadedTimestamp: Date.now(),
            data: reportComments,
        };
    });
};

export default reducer;
